import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError, getTeamAvatarUrl, handleTeamAvatarError } from '../utils/avatars';
import Pagination from './Pagination';
import CountryDisplay from './CountryDisplay';
import './UsersList.css';
import './TeamMembersModal.css';

// Бейдж роли участника
const RoleBadge = ({ role, t }) => {
  if (role === 'captain') {
    return <span className="role-badge role-captain">{t('teams.roles.captain')}</span>;
  }
  return <span className="role-badge role-member">{t('teams.roles.member')}</span>;
};

const TeamMembersModal = ({ team, onClose }) => {
  const { user: currentUser, setUser } = useAuth();
  const { t, language } = useLanguage();

  const [members, setMembers]           = useState([]);
  const [manager, setManager]           = useState(null);
  const [captain, setCaptain]           = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  // Команда под событие соревновательной не бывает — кнопок смены нет
  const [isEventTeam, setIsEventTeam]   = useState(Boolean(team.event_team));
  const [pagination, setPagination]     = useState({ current_page: 1, per_page: 20, total_pages: 1, total_count: 0 });
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [isMobile, setIsMobile]         = useState(window.innerWidth <= 768);
  const [activeTooltip, setActiveTooltip]         = useState(null);
  const [tooltipPosition, setTooltipPosition]     = useState({ top: 0, left: 0 });
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd]     = useState(null);

  const fetchMembers = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/teams/${team.id}/members?page=${page}`);
      setMembers(res.data.members);
      setManager(res.data.manager);
      setCaptain(res.data.captain);
      setCurrentUserRole(res.data.current_user_role);
      setIsEventTeam(Boolean(res.data.team?.event_team));
      setPagination(res.data.pagination);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Права доступа ---

  const isManager  = currentUserRole === 'manager' || currentUser?.admin;
  const isCaptain  = currentUserRole === 'captain';
  const canManage  = isManager || isCaptain; // менеджер или капитан

  const canAssignCaptain = (member) =>
    isManager && member.role !== 'captain' && member.id !== currentUser?.id;

  const canTransferCaptaincy = (member) =>
    isCaptain && member.role !== 'captain' && member.id !== currentUser?.id;

  const canRemoveMember = (member) =>
    isManager && member.id !== currentUser?.id;

  // Кнопка активна только если бэкенд разрешил смену (нет активных командных регистраций)
  const canSetCompetitionTeam = (member) =>
    !isEventTeam && member.id === currentUser?.id && !member.competition_team && member.can_change_competition_team;

  // Кнопка видна (но заблокирована) если пользователь — это текущий юзер, не соревновательная команда,
  // но смена заблокирована участием в активном соревновании
  const showBlockedCompetitionTeamBtn = (member) =>
    !isEventTeam && member.id === currentUser?.id && !member.competition_team && !member.can_change_competition_team;

  // --- Действия ---

  const handleAssignCaptain = async (userId) => {
    if (!window.confirm(t('teams.confirmAssignCaptain'))) return;
    try {
      await axios.post(`/api/teams/${team.id}/assign_captain`, { user_id: userId });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || t('common.error'));
    }
  };

  const handleTransferCaptaincy = async (userId) => {
    if (!window.confirm(t('teams.confirmTransferCaptaincy'))) return;
    try {
      await axios.post(`/api/teams/${team.id}/transfer_captaincy`, { user_id: userId });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || t('common.error'));
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm(t('users.confirmRemoveFromTeam'))) return;
    try {
      await axios.delete(`/api/teams/${team.id}/remove_member`, { data: { user_id: userId } });
      fetchMembers();
    } catch (err) {
      alert(err.response?.data?.error || t('common.error'));
    }
  };

  const handleSetCompetitionTeam = async (membershipId) => {
    if (!window.confirm(t('teams.confirmSetCompetitionTeam'))) return;
    try {
      const res = await axios.post(`/api/team_memberships/${membershipId}/set_competition_team`);
      fetchMembers();
      // Обновляем данные текущего пользователя
      if (setUser && res.data.team_id) {
        setUser(prev => ({ ...prev, team_id: res.data.team_id }));
      }
    } catch (err) {
      alert(err.response?.data?.error || t('common.error'));
    }
  };

  // --- Tooltip ---

  const handleMouseEnter = (e, tooltipId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const labels = {
      assign_captain:             t('teams.assignCaptain'),
      transfer_captaincy:         t('teams.transferCaptaincy'),
      remove:                     t('users.removeFromTeam'),
      competition_team_blocked:   t('teams.cannotChangeCompetitionTeam'),
      competition_team:           t('teams.setCompetitionTeam')
    };
    const key = Object.keys(labels).find(k => tooltipId.startsWith(k));
    const text = key ? labels[key] : '';

    const tmp = document.createElement('div');
    tmp.className = 'tooltip-global';
    tmp.style.cssText = 'visibility:hidden;position:fixed;top:-9999px';
    tmp.textContent = text;
    document.body.appendChild(tmp);
    const w = tmp.offsetWidth;
    document.body.removeChild(tmp);

    setTooltipPosition({ top: rect.top - 40, left: rect.left + rect.width / 2 - w / 2 });
    setActiveTooltip(tooltipId);
  };

  const handleMouseLeave = () => setActiveTooltip(null);

  // --- Swipe to close (mobile) ---
  const onTouchStart = (e) => { setTouchEnd(null); setTouchStart(e.targetTouches[0].clientY); };
  const onTouchMove  = (e) => setTouchEnd(e.targetTouches[0].clientY);
  const onTouchEnd   = () => {
    if (touchStart && touchEnd && touchStart - touchEnd < -50) onClose();
  };

  const displayName = (m) =>
    language !== 'ru' && m.first_name_eng && m.last_name_eng
      ? `${m.first_name_eng} ${m.last_name_eng}`
      : `${m.first_name} ${m.last_name}`;

  // --- Рендер строки действий ---
  const renderActions = (member) => (
    <div className="member-actions">
      {/* Назначить капитаном (менеджер) */}
      {canAssignCaptain(member) && (
        <button
          onClick={() => handleAssignCaptain(member.id)}
          onMouseEnter={(e) => handleMouseEnter(e, `assign_captain-${member.id}`)}
          onMouseLeave={handleMouseLeave}
          className="btn-icon btn-icon-captain"
          aria-label={t('teams.assignCaptain')}
        >
          {/* Корона */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 19l2-9 4 4 4-8 4 8 4-4 2 9H2z"/>
          </svg>
        </button>
      )}
      {/* Передать капитанство (капитан) */}
      {canTransferCaptaincy(member) && (
        <button
          onClick={() => handleTransferCaptaincy(member.id)}
          onMouseEnter={(e) => handleMouseEnter(e, `transfer_captaincy-${member.id}`)}
          onMouseLeave={handleMouseLeave}
          className="btn-icon btn-icon-captain"
          aria-label={t('teams.transferCaptaincy')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 19l2-9 4 4 4-8 4 8 4-4 2 9H2z"/>
          </svg>
        </button>
      )}
      {/* Исключить из команды (менеджер) */}
      {canRemoveMember(member) && (
        <button
          onClick={() => handleRemoveMember(member.id)}
          onMouseEnter={(e) => handleMouseEnter(e, `remove-${member.id}`)}
          onMouseLeave={handleMouseLeave}
          className="btn-icon btn-icon-remove"
          aria-label={t('users.removeFromTeam')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <line x1="22" y1="11" x2="16" y2="11"/>
          </svg>
        </button>
      )}
      {/* Установить как соревновательную команду (сам участник) */}
      {canSetCompetitionTeam(member) && (
        <button
          onClick={() => handleSetCompetitionTeam(member.membership_id)}
          onMouseEnter={(e) => handleMouseEnter(e, `competition_team-${member.id}`)}
          onMouseLeave={handleMouseLeave}
          className="btn-icon btn-icon-competition"
          aria-label={t('teams.setCompetitionTeam')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8l1.5 3 3.5.5-2.5 2.5.5 3.5L12 16l-3 1.5.5-3.5L7 11.5l3.5-.5z"/>
          </svg>
        </button>
      )}
      {/* Заблокированная кнопка смены команды — участник активно участвует в соревновании */}
      {showBlockedCompetitionTeamBtn(member) && (
        <button
          disabled
          onMouseEnter={(e) => handleMouseEnter(e, `competition_team_blocked-${member.id}`)}
          onMouseLeave={handleMouseLeave}
          className="btn-icon btn-icon-competition btn-icon-disabled"
          aria-label={t('teams.cannotChangeCompetitionTeam')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 8l1.5 3 3.5.5-2.5 2.5.5 3.5L12 16l-3 1.5.5-3.5L7 11.5l3.5-.5z"/>
          </svg>
        </button>
      )}
    </div>
  );

  const showActionsColumn = canManage || members.some(m => canSetCompetitionTeam(m) || showBlockedCompetitionTeamBtn(m));

  return (
    <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && onClose()}>
      <div
        className="modal-content team-members-modal"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="modal-header">
          <div className="team-info">
            <img
              src={getTeamAvatarUrl(team.avatar, team.name)}
              alt={team.name}
              className="team-avatar-modal"
              onError={(e) => handleTeamAvatarError(e, team.name)}
            />
            <h2>{team.name} — {t('teams.members')}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {loading && members.length === 0 ? (
            <div className="loading">{t('common.loading')}</div>
          ) : error ? (
            <div className="error">{error}</div>
          ) : (
            <>
              <div className="members-stats">
                {t('teams.totalMembers')}: {pagination.total_count}
                {captain && (
                  <span className="captain-info">
                    &nbsp;·&nbsp;{t('teams.roles.captain')}: <strong>{captain.nickname}</strong>
                  </span>
                )}
                {!captain && (
                  <span className="no-captain-info">&nbsp;·&nbsp;{t('teams.noCaptain')}</span>
                )}
              </div>

              {/* Менеджер команды — выделен отдельно */}
              {manager && (
                <div className="manager-row">
                  <img
                    src={getUserAvatarUrl(manager.avatar)}
                    alt={manager.nickname}
                    className="manager-avatar"
                    onError={handleUserAvatarError}
                  />
                  <div className="manager-info">
                    <span className="manager-name">
                      {language !== 'ru' && manager.first_name_eng && manager.last_name_eng
                        ? `${manager.first_name_eng} ${manager.last_name_eng}`
                        : `${manager.first_name} ${manager.last_name}`}
                    </span>
                    <span className="role-badge role-manager">{t('teams.roles.manager')}</span>
                  </div>
                </div>
              )}

              {isMobile ? (
                <div className="members-cards-container">
                  {members.map((member) => (
                    <div key={member.id} className={`member-card ${member.blocked_at ? 'blocked-user' : ''}`}>
                      <div className="member-card-header">
                        <img
                          src={getUserAvatarUrl(member.avatar)}
                          alt={member.nickname}
                          className="member-card-avatar"
                          onError={handleUserAvatarError}
                        />
                        <div className="member-card-info">
                          <div className="member-card-name">{displayName(member)}</div>
                          <div className="member-card-nickname">@{member.nickname}</div>
                          <RoleBadge role={member.role} t={t} />
                          {member.competition_team && (
                            <span className="competition-badge">{t('teams.competitionTeam')}</span>
                          )}
                        </div>
                      </div>
                      <div className="member-card-body">
                        <div className="member-card-row">
                          <span className="member-card-label">{t('users.country')}:</span>
                          <CountryDisplay country={member.country} language={language} />
                        </div>
                      </div>
                      {(canManage || canSetCompetitionTeam(member) || showBlockedCompetitionTeamBtn(member)) && (
                        <div className="member-card-footer">
                          {renderActions(member)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="users-table">
                    <thead>
                      <tr>
                        <th>{t('users.fullName')}</th>
                        <th>{t('users.nickname')}</th>
                        <th>{t('users.country')}</th>
                        <th>{t('teams.role')}</th>
                        {showActionsColumn && <th>{t('users.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((member) => (
                        <tr key={member.id} className={member.blocked_at ? 'blocked-user' : ''}>
                          <td>
                            <div className="user-name">
                              <img
                                src={getUserAvatarUrl(member.avatar)}
                                alt={member.nickname}
                                className="user-avatar-small"
                                onError={handleUserAvatarError}
                              />
                              {displayName(member)}
                              {member.competition_team && member.id === currentUser?.id && (
                                <span className="competition-badge-small" title={t('teams.competitionTeam')}>★</span>
                              )}
                            </div>
                          </td>
                          <td>{member.nickname}</td>
                          <td>
                            <CountryDisplay country={member.country} language={language} />
                          </td>
                          <td>
                            <RoleBadge role={member.role} t={t} />
                          </td>
                          {showActionsColumn && (
                            <td>{renderActions(member)}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.total_pages > 1 && (
                <Pagination pagination={pagination} onPageChange={(p) => fetchMembers(p)} t={t} />
              )}
            </>
          )}
        </div>
      </div>

      {activeTooltip && (
        <div
          className="tooltip tooltip-global"
          style={{ top: `${tooltipPosition.top}px`, left: `${tooltipPosition.left}px`, opacity: 1 }}
        >
          {activeTooltip.startsWith('assign_captain')          && t('teams.assignCaptain')}
          {activeTooltip.startsWith('transfer_captaincy')      && t('teams.transferCaptaincy')}
          {activeTooltip.startsWith('remove')                  && t('users.removeFromTeam')}
          {activeTooltip.startsWith('competition_team_blocked') && t('teams.cannotChangeCompetitionTeam')}
          {activeTooltip.startsWith('competition_team') && !activeTooltip.startsWith('competition_team_blocked') && t('teams.setCompetitionTeam')}
        </div>
      )}
    </div>
  );
};

export default TeamMembersModal;
